import { Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDemoState } from "../store/useDemoStore";
import { addWorkingDaysToKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { CALL_OUT_DAY_OFFSET } from "@caredna-marlin/utils/homeHealthDemo/fixtures/cast";

interface NavItem {
  step: string;
  label: string;
  to?: string;
  patientRef?: string;
  /** The command center on a day other than today: the call-out day, a working-day offset from demo day. */
  commandDayOffset?: number;
}

/**
 * The screens each script step lands on, in script order, so the presenter never
 * types a URL. Patient pages resolve through the tenant bindings. The
 * Sandbox is absent because it no longer exists: the tab was cut on 2026-09-22
 * and Branch Capacity is Who is working and Heat map.
 */
const ITEMS: NavItem[] = [
  { step: "Open", label: "Referrals", to: "/referrals" },
  { step: "1.2", label: "Referral panel · Alvarez", to: "/referrals/ref-alvarez" },
  { step: "1.3", label: "Pending admissions", to: "/pending-admissions" },
  { step: "2.1", label: "Patient · Whitfield", patientRef: "census-01" },
  { step: "3.1", label: "My week", to: "/my-week" },
  { step: "5.1", label: "Command center · today", to: "/command" },
  { step: "5.2", label: "Command center · call-out day", commandDayOffset: CALL_OUT_DAY_OFFSET },
  { step: "6.1", label: "Who is working", to: "/capacity/roster" },
  { step: "6.1", label: "Heat map", to: "/capacity/heatmap" },
  { step: "R", label: "Worklist · Compliance", to: "/visit-alerts?tab=compliance" },
];

export function ScriptNav({ onNavigate }: { onNavigate?: () => void }) {
  const state = useDemoState();
  const navigate = useNavigate();
  const patientIds = state.meta.bindings.patientIdsByRef;
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2" gutterBottom>
        Script screens
      </Typography>
      {ITEMS.map((item) => {
        const to =
          item.to ??
          (item.commandDayOffset !== undefined ? `/command?date=${addWorkingDaysToKey(state.meta.demoToday, item.commandDayOffset)}` : undefined) ??
          (item.patientRef && patientIds[item.patientRef] !== undefined ? `/patients/${patientIds[item.patientRef]}` : undefined);
        const name = item.patientRef ? state.patients[item.patientRef]?.displayName : undefined;
        return (
          <Button
            key={`${item.step}-${item.label}`}
            size="small"
            variant="text"
            disabled={!to}
            onClick={() => {
              if (!to) return;
              navigate(to);
              onNavigate?.();
            }}
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0, textAlign: "left" }}>
              {item.step}
            </Typography>
            {item.label}
            {name ? ` — ${name}` : ""}
            {!to && item.patientRef ? " (not bound)" : ""}
          </Button>
        );
      })}
    </Stack>
  );
}
